/**
 * OpsMapTour.jsx
 * In-app guided tour overlay for the Ops Map flight controller workstation.
 * Uses a CSS spotlight (box-shadow cutout) with fixed-position callout cards.
 * No external dependencies.
 */
import { useState, useEffect } from "react";
import { X, ChevronRight, ChevronLeft, MapPin, Layers, Zap, Clock, BarChart2, HelpCircle } from "lucide-react";

const TOUR_KEY = "fs_ops_tour_v1";

// Each step defines:
//   spotlight: {top, left, width, height} in px (or % strings)
//   callout:   {side: "top"|"bottom"|"left"|"right", top?, left?, right?, bottom?}
const STEPS = [
  {
    id: "welcome",
    title: "Welcome to Ops Map",
    body: "Your real-time flight controller workstation. This tour covers the key panels — it takes about 60 seconds. Skip anytime.",
    icon: MapPin,
    spotlight: null,   // no spotlight on welcome
    callout: { top: "50%", left: "50%", transform: "translate(-50%,-50%)" },
    wide: true,
  },
  {
    id: "sidebar",
    title: "Disruption Feed",
    body: "All active disruptions, sorted by severity. Use the view tabs to switch between Disruptions and IRROPS events. Click any item to see full cascade impact on the map.",
    icon: Layers,
    spotlight: { top: 0, left: 0, width: 288, height: "100vh" },
    callout: { top: "50%", left: 310, transform: "translateY(-50%)" },
  },
  {
    id: "network",
    title: "Live Network Map",
    body: "Each arc is a live route. Colors indicate disruption severity — red = critical impact, amber = downstream delay. Click any arc or airport node to drill into the event.",
    icon: MapPin,
    spotlight: { top: 56, left: 288, right: 0, height: "calc(100vh - 56px)" },
    callout: { top: "42%", left: "50%", transform: "translateX(-50%)" },
  },
  {
    id: "timeslider",
    title: "Time Projection Slider",
    body: "Drag this slider to project the network 0–6 hours forward. Routes shift from blue → amber → red as crew FDP limits deplete — plan ahead before assigning reserve crew.",
    icon: Clock,
    spotlight: { bottom: 8, right: 8, width: 290, height: 90 },
    callout: { bottom: 116, right: 8 },
  },
  {
    id: "stats",
    title: "Network Health Stats",
    body: "Live counts — airports tracked, active route arcs, and in-flight aircraft. In AOG mode this bar switches to show the affected tail number and fault type.",
    icon: BarChart2,
    spotlight: { bottom: 8, left: 288, width: 340, height: 36 },
    callout: { bottom: 60, left: 288 },
  },
  {
    id: "democontrols",
    title: "Scenario Injection Panel",
    body: "The floating panel (bottom-right of screen) lets you inject real AOG, weather, or crew sick-call scenarios. Hit 'Perfect World Reset' for a clean baseline, then inject any scenario to see the full cascade unfold live.",
    icon: Zap,
    spotlight: null,
    callout: { bottom: 80, right: 16 },
    wide: true,
  },
  {
    id: "aog",
    title: "AOG Response Flow",
    body: "When an Aircraft AOG is injected, the right panel opens with the full day plan (sector arcs coloured by status), two recovery options, crew FDP impact, and AI-powered reintegration analysis via Claude.",
    icon: Zap,
    spotlight: null,
    callout: { top: "50%", left: "50%", transform: "translate(-50%,-50%)" },
    wide: true,
  },
];

function SpotlightBox({ spotlight }) {
  if (!spotlight) return null;
  const style = {
    position: "fixed",
    boxShadow: "0 0 0 9999px rgba(0,0,0,0.72)",
    borderRadius: "4px",
    border: "2px solid rgba(59,130,246,0.7)",
    pointerEvents: "none",
    zIndex: 9998,
    transition: "all 0.3s ease",
    ...spotlight,
  };
  // Convert numeric values to px strings
  const final = {};
  for (const [k, v] of Object.entries(style)) {
    final[k] = typeof v === "number" ? `${v}px` : v;
  }
  return <div style={final} />;
}

function CalloutCard({ step, stepIdx, total, onNext, onPrev, onClose }) {
  const Icon = step.icon;
  const style = {
    position: "fixed",
    zIndex: 9999,
    width: step.wide ? 380 : 320,
    ...Object.fromEntries(
      Object.entries(step.callout).map(([k, v]) => [k, typeof v === "number" ? `${v}px` : v])
    ),
  };

  return (
    <div style={style} className="bg-card border border-border/80 rounded shadow-2xl overflow-hidden"
      data-testid={`tour-step-${step.id}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-primary/10 border-b border-primary/20">
        <div className="flex items-center gap-2">
          <Icon size={13} className="text-primary" />
          <span className="text-xs font-black uppercase tracking-widest text-primary">{step.title}</span>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="tour-close-btn">
          <X size={13} />
        </button>
      </div>
      {/* Body */}
      <div className="px-4 py-3">
        <p className="text-xs text-foreground/90 leading-relaxed">{step.body}</p>
      </div>
      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-2.5 border-t border-border/60">
        <div className="flex gap-1">
          {STEPS.map((_, i) => (
            <div key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === stepIdx ? "bg-primary" : "bg-border"}`} />
          ))}
        </div>
        <div className="flex items-center gap-2">
          {stepIdx > 0 && (
            <button onClick={onPrev}
              className="flex items-center gap-1 px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground border border-border rounded-sm transition-colors"
              data-testid="tour-prev-btn">
              <ChevronLeft size={11} /> Back
            </button>
          )}
          {stepIdx < total - 1 ? (
            <button onClick={onNext}
              className="flex items-center gap-1 px-3 py-1 text-xs bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 transition-colors font-semibold"
              data-testid="tour-next-btn">
              Next <ChevronRight size={11} />
            </button>
          ) : (
            <button onClick={onClose}
              className="flex items-center gap-1 px-3 py-1 text-xs bg-green-600 text-white rounded-sm hover:bg-green-500 transition-colors font-semibold"
              data-testid="tour-done-btn">
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function OpsMapTourTrigger({ onStart }) {
  return (
    <button
      onClick={onStart}
      className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-border hover:border-primary/40 rounded-sm px-2.5 py-1.5 transition-colors"
      data-testid="ops-tour-btn"
      title="Take a guided tour of the Ops Map">
      <HelpCircle size={12} /> Tour
    </button>
  );
}

export default function OpsMapTour({ onFinish }) {
  const [step, setStep] = useState(0);

  const handleClose = () => {
    localStorage.setItem(TOUR_KEY, "done");
    onFinish();
  };
  const handleNext = () => setStep(s => Math.min(s + 1, STEPS.length - 1));
  const handlePrev = () => setStep(s => Math.max(s - 1, 0));

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") handleClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const current = STEPS[step];

  return (
    <>
      {/* Click-through overlay (only dims, doesn't block interaction inside spotlight) */}
      <div
        className="fixed inset-0 z-[9997]"
        style={{ background: current.spotlight ? "transparent" : "rgba(0,0,0,0.72)" }}
        onClick={handleClose}
        data-testid="tour-overlay"
      />
      <SpotlightBox spotlight={current.spotlight} />
      <CalloutCard
        step={current}
        stepIdx={step}
        total={STEPS.length}
        onNext={handleNext}
        onPrev={handlePrev}
        onClose={handleClose}
      />
    </>
  );
}

/** Returns true if user hasn't seen the tour yet */
export function shouldShowTourOnLoad() {
  return !localStorage.getItem(TOUR_KEY);
}
